import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGarmin, type GarminStatus } from '../hooks/useGarmin.ts';

/** Garmin's brand guidelines: the full app name and its tile when connecting. */
export const GARMIN_CONNECT_NAME = 'Garmin Connect™';

const CARD = 'rounded-lg border border-(--color-border) bg-(--color-bg-tertiary) p-4 flex flex-col gap-3';
const SECONDARY_BUTTON =
  'px-3 py-1.5 rounded-lg text-xs font-semibold bg-(--color-bg-secondary) text-(--color-text-secondary) border border-(--color-border) hover:border-red-400 hover:text-red-400 disabled:opacity-50 transition-all';

function privacyUrl(language: string): string {
  return language.startsWith('uk') ? '/uk/privacy.html' : '/privacy.html';
}

function Header({ badge }: Readonly<{ badge?: string }>) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        <img src="/garmin-connect.png" alt="" width={28} height={28} className="rounded-md" />
        <h3 className="text-sm font-semibold text-(--color-text-primary)">{GARMIN_CONNECT_NAME}</h3>
      </div>
      {badge && <span className="text-xs text-(--color-text-secondary)">{badge}</span>}
    </div>
  );
}

function AiSwitch({
  checked,
  disabled,
  onChange,
}: Readonly<{ checked: boolean; disabled?: boolean; onChange: (value: boolean) => void }>) {
  const { t } = useTranslation();
  return (
    <label className="flex items-start gap-2 text-xs text-(--color-text-primary) cursor-pointer">
      <input
        type="checkbox"
        aria-label={t('garmin.aiSwitch')}
        className="mt-0.5"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        <span className="font-semibold">{t('garmin.aiSwitch')}</span>
        <span className="block text-(--color-text-secondary)">{t('garmin.aiSwitchHint')}</span>
      </span>
    </label>
  );
}

function ConnectForm() {
  const { t, i18n } = useTranslation();
  const { connect } = useGarmin();
  const [ai, setAi] = useState(false);
  return (
    <div className={CARD}>
      <Header badge={t('garmin.notConnected')} />
      <ul className="text-xs text-(--color-text-secondary) list-disc pl-4 flex flex-col gap-1">
        <li>{t('garmin.explainRead')}</li>
        <li>{t('garmin.explainStore')}</li>
        <li>{t('garmin.explainAi')}</li>
        <li>{t('garmin.explainStop')}</li>
      </ul>
      <AiSwitch checked={ai} onChange={setAi} />
      <p className="text-xs text-(--color-text-secondary)">
        {t('garmin.agreeNote')}{' '}
        <a href={privacyUrl(i18n.language)} target="_blank" rel="noreferrer" className="text-(--color-accent) hover:underline">
          {t('garmin.privacyLink')}
        </a>
      </p>
      <button
        onClick={() => connect.mutate(ai)}
        disabled={connect.isPending}
        className="self-start px-4 py-2 rounded-lg text-sm font-bold text-white bg-[#007cc3] hover:brightness-110 disabled:opacity-50 transition-all"
      >
        {connect.isPending ? `${t('garmin.connecting')}…` : t('garmin.connect', { name: GARMIN_CONNECT_NAME })}
      </button>
    </div>
  );
}

function DisconnectPanel({ rides, connected }: Readonly<{ rides: number; connected: boolean }>) {
  const { t } = useTranslation();
  const { disconnect } = useGarmin();
  const [open, setOpen] = useState(false);
  const [deleteRides, setDeleteRides] = useState(!connected);
  const action = connected ? t('garmin.disconnect') : t('garmin.deleteRides');
  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className={`self-start ${SECONDARY_BUTTON}`}>
        {action}
      </button>
    );
  }
  return (
    <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 flex flex-col gap-2 text-xs">
      <p className="text-(--color-text-primary)">
        {connected ? t('garmin.disconnectConfirm') : t('garmin.deleteRidesConfirm', { count: rides })}
      </p>
      {connected && rides > 0 && (
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={deleteRides} onChange={(e) => setDeleteRides(e.target.checked)} />
          {t('garmin.alsoDeleteRides', { count: rides })}
        </label>
      )}
      <div className="flex gap-2">
        <button
          onClick={() => disconnect.mutate(deleteRides, { onSettled: () => setOpen(false) })}
          disabled={disconnect.isPending}
          className="px-3 py-1.5 rounded-lg font-semibold bg-red-500/80 text-white disabled:opacity-50"
        >
          {action}
        </button>
        <button onClick={() => setOpen(false)} className={SECONDARY_BUTTON}>
          {t('garmin.cancel')}
        </button>
      </div>
    </div>
  );
}

function LinkedDetails({ data }: Readonly<{ data: GarminStatus }>) {
  const { t } = useTranslation();
  const last = data.last_activity_at
    ? new Date(data.last_activity_at).toLocaleDateString()
    : t('garmin.noRidesYet');
  return (
    <div className="text-xs text-(--color-text-secondary) flex flex-col gap-1">
      <span>{t('garmin.lastRide', { when: last })}</span>
      <span>{t('garmin.newRidesOnly')}</span>
      {data.activity_export === false && (
        <span className="text-amber-400">{t('garmin.permissionMissing')}</span>
      )}
    </div>
  );
}

function Linked({ data }: Readonly<{ data: GarminStatus }>) {
  const { t } = useTranslation();
  const { setAiConsent } = useGarmin();
  return (
    <div className={CARD}>
      <Header badge={t('garmin.connected')} />
      <LinkedDetails data={data} />
      <AiSwitch
        checked={data.ai_consent === true}
        disabled={setAiConsent.isPending}
        onChange={(value) => setAiConsent.mutate(value)}
      />
      {data.ai_consent && (
        <p className="text-[11px] text-(--color-text-secondary)">{t('garmin.aiWithdrawNote')}</p>
      )}
      <DisconnectPanel rides={data.rides ?? 0} connected />
    </div>
  );
}

function NeedsReconnect({ data }: Readonly<{ data: GarminStatus }>) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs text-amber-400">{t('garmin.needsReconnect')}</p>
      <ConnectForm />
      <DisconnectPanel rides={data.rides ?? 0} connected />
    </div>
  );
}

/** Garmin Connect card for Profile → Integrations (shown when ``features.garmin``). */
export function GarminConnect() {
  const { t } = useTranslation();
  const { status } = useGarmin();
  const data = status.data;

  if (status.isLoading) {
    return (
      <div className={CARD}>
        <p className="text-sm text-(--color-text-secondary)">{t('garmin.loading')}</p>
      </div>
    );
  }
  if (!data?.available) return null;
  if (!data.configured) {
    return (
      <div className={CARD}>
        <Header />
        <p className="text-xs text-(--color-text-secondary)">{t('garmin.notConfigured')}</p>
      </div>
    );
  }
  if (data.needs_reconnect) return <NeedsReconnect data={data} />;
  if (data.connected) return <Linked data={data} />;
  return (
    <div className="flex flex-col gap-2">
      <ConnectForm />
      {(data.rides ?? 0) > 0 && <DisconnectPanel rides={data.rides ?? 0} connected={false} />}
    </div>
  );
}
